import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  compareStaffTasks,
  isStaffTaskEntityType,
  isStaffTaskPriority,
  isStaffTaskStatus,
  type StaffTask,
  type StaffTaskAssignee,
  type StaffTaskComment,
  type StaffTaskEntityType,
  type StaffTaskLink,
  type StaffTaskPriority,
  type StaffTaskStatus,
} from '@/lib/staffTasks';

export const staffTasksQueryKey = (clinicId: string | null) =>
  ['staff-tasks', clinicId] as const;

export const staffTaskCommentsQueryKey = (taskId: string | null) =>
  ['staff-task-comments', taskId] as const;

export const myOpenTaskCountQueryKey = (clinicId: string | null, userId: string | null) =>
  ['staff-tasks', 'my-open-count', clinicId, userId] as const;

type UserJoin = { name: string | null; email: string } | null;

type AssigneeJoin = {
  user_id: string;
  user: UserJoin;
};

type LinkJoin = {
  entity_type: string;
  entity_id: string;
};

type TaskRow = {
  id: string;
  clinic_id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  deadline_date: string | null;
  deadline_time: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  completed_by: string | null;
  created_by_user: UserJoin;
  completed_by_user: UserJoin;
  assignees: AssigneeJoin[] | null;
  links: LinkJoin[] | null;
};

const TASK_SELECT =
  'id, clinic_id, title, description, status, priority, deadline_date, deadline_time, ' +
  'created_by, created_at, updated_at, completed_at, completed_by, ' +
  'created_by_user:users!staff_tasks_created_by_fkey(name,email), ' +
  'completed_by_user:users!staff_tasks_completed_by_fkey(name,email), ' +
  'assignees:staff_task_assignees(user_id, user:users!staff_task_assignees_user_id_fkey(name,email)), ' +
  'links:staff_task_links(entity_type, entity_id)';

function displayName(user: UserJoin): string {
  return user?.name?.trim() || user?.email || '';
}

function mapTask(row: TaskRow): StaffTask {
  const assignees: StaffTaskAssignee[] = (row.assignees ?? []).map((assignee) => ({
    user_id: assignee.user_id,
    name: displayName(assignee.user) || assignee.user_id,
  }));
  const links: StaffTaskLink[] = [];
  for (const link of row.links ?? []) {
    if (isStaffTaskEntityType(link.entity_type) && link.entity_id) {
      links.push({ entity_type: link.entity_type, entity_id: link.entity_id });
    }
  }
  return {
    id: row.id,
    clinic_id: row.clinic_id,
    title: row.title,
    description: row.description,
    status: isStaffTaskStatus(row.status) ? row.status : 'open',
    priority: isStaffTaskPriority(row.priority) ? row.priority : 'normal',
    deadline_date: row.deadline_date,
    deadline_time: row.deadline_time,
    created_by: row.created_by,
    created_by_name: displayName(row.created_by_user) || null,
    created_at: row.created_at,
    updated_at: row.updated_at,
    completed_at: row.completed_at,
    completed_by: row.completed_by,
    completed_by_name: displayName(row.completed_by_user) || null,
    assignees,
    links,
  };
}

async function fetchStaffTasks(clinicId: string): Promise<StaffTask[]> {
  const { data, error } = await supabase
    .from('staff_tasks')
    .select(TASK_SELECT)
    .eq('clinic_id', clinicId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return ((data ?? []) as unknown as TaskRow[]).map(mapTask).sort((a, b) => compareStaffTasks(a, b));
}

function invalidateStaffTasks(queryClient: ReturnType<typeof useQueryClient>, clinicId: string) {
  queryClient.invalidateQueries({ queryKey: staffTasksQueryKey(clinicId) });
  queryClient.invalidateQueries({ queryKey: ['staff-tasks', 'my-open-count', clinicId] });
}

export function useStaffTasks(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: staffTasksQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<StaffTask[]> => {
      if (!clinicId) return [];
      return fetchStaffTasks(clinicId);
    },
    staleTime: 15_000,
  });
}

export function useStaffTasksForEntity(
  clinicId: string | null | undefined,
  entityType: StaffTaskEntityType | null,
  entityId: string | null | undefined,
) {
  const all = useStaffTasks(clinicId);
  const tasks =
    all.data?.filter((task) =>
      Boolean(
        entityType &&
          entityId &&
          task.links.some((link) => link.entity_type === entityType && link.entity_id === entityId),
      ),
    ) ?? [];
  return { ...all, data: entityType && entityId ? tasks : [] };
}

export function useMyOpenTaskCount(clinicId: string | null | undefined, userId: string | null | undefined) {
  return useQuery({
    queryKey: myOpenTaskCountQueryKey(clinicId ?? null, userId ?? null),
    enabled: Boolean(clinicId && userId),
    queryFn: async (): Promise<number> => {
      if (!clinicId || !userId) return 0;
      const { data, error } = await supabase
        .from('staff_task_assignees')
        .select('task_id, staff_tasks!inner(status, clinic_id)')
        .eq('clinic_id', clinicId)
        .eq('user_id', userId);
      if (error) throw error;
      type Row = { staff_tasks: { status: string } | { status: string }[] | null };
      let count = 0;
      for (const row of (data ?? []) as unknown as Row[]) {
        const parent = Array.isArray(row.staff_tasks) ? row.staff_tasks[0] : row.staff_tasks;
        if (parent && parent.status !== 'done') count += 1;
      }
      return count;
    },
    staleTime: 15_000,
  });
}

export interface StaffTaskWriteInput {
  clinic_id: string;
  title: string;
  description?: string | null;
  status?: StaffTaskStatus;
  priority?: StaffTaskPriority;
  deadline_date?: string | null;
  deadline_time?: string | null;
  assignee_ids: string[];
  links: StaffTaskLink[];
}

async function replaceAssignees(taskId: string, clinicId: string, assigneeIds: string[]) {
  const { error: delError } = await supabase
    .from('staff_task_assignees')
    .delete()
    .eq('task_id', taskId);
  if (delError) throw delError;
  const unique = [...new Set(assigneeIds.filter(Boolean))];
  if (unique.length === 0) return;
  const { error } = await supabase.from('staff_task_assignees').insert(
    unique.map((user_id) => ({
      task_id: taskId,
      user_id,
      clinic_id: clinicId,
    })),
  );
  if (error) throw error;
}

async function replaceLinks(taskId: string, clinicId: string, links: StaffTaskLink[]) {
  const { error: delError } = await supabase.from('staff_task_links').delete().eq('task_id', taskId);
  if (delError) throw delError;
  const unique = new Map<string, StaffTaskLink>();
  for (const link of links) {
    if (!link.entity_id.trim()) continue;
    unique.set(`${link.entity_type}:${link.entity_id}`, {
      entity_type: link.entity_type,
      entity_id: link.entity_id.trim(),
    });
  }
  if (unique.size === 0) return;
  const { error } = await supabase.from('staff_task_links').insert(
    [...unique.values()].map((link) => ({
      task_id: taskId,
      clinic_id: clinicId,
      entity_type: link.entity_type,
      entity_id: link.entity_id,
    })),
  );
  if (error) throw error;
}

export function useCreateStaffTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: StaffTaskWriteInput) => {
      const { data, error } = await supabase
        .from('staff_tasks')
        .insert({
          clinic_id: input.clinic_id,
          title: input.title.trim(),
          description: input.description?.trim() || null,
          status: input.status ?? 'open',
          priority: input.priority ?? 'normal',
          deadline_date: input.deadline_date || null,
          deadline_time: input.deadline_time || null,
        })
        .select('id')
        .single();
      if (error) throw error;
      const taskId = data.id;
      await replaceAssignees(taskId, input.clinic_id, input.assignee_ids);
      await replaceLinks(taskId, input.clinic_id, input.links);
      return taskId;
    },
    onSuccess: (_id, variables) => {
      invalidateStaffTasks(queryClient, variables.clinic_id);
    },
  });
}

export function useUpdateStaffTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: StaffTaskWriteInput & { task_id: string }) => {
      const { error } = await supabase
        .from('staff_tasks')
        .update({
          title: input.title.trim(),
          description: input.description?.trim() || null,
          status: input.status ?? 'open',
          priority: input.priority ?? 'normal',
          deadline_date: input.deadline_date || null,
          deadline_time: input.deadline_time || null,
        })
        .eq('id', input.task_id);
      if (error) throw error;
      await replaceAssignees(input.task_id, input.clinic_id, input.assignee_ids);
      await replaceLinks(input.task_id, input.clinic_id, input.links);
    },
    onSuccess: (_void, variables) => {
      invalidateStaffTasks(queryClient, variables.clinic_id);
    },
  });
}

export function useToggleStaffTaskDone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { task_id: string; clinic_id: string; done: boolean }) => {
      const { error } = await supabase
        .from('staff_tasks')
        .update({ status: input.done ? 'done' : 'open' })
        .eq('id', input.task_id);
      if (error) throw error;
    },
    onSuccess: (_void, variables) => {
      invalidateStaffTasks(queryClient, variables.clinic_id);
    },
  });
}

export function useDeleteStaffTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { task_id: string; clinic_id: string }) => {
      const { error } = await supabase.from('staff_tasks').delete().eq('id', input.task_id);
      if (error) throw error;
    },
    onSuccess: (_void, variables) => {
      invalidateStaffTasks(queryClient, variables.clinic_id);
    },
  });
}

export function useStaffTaskComments(taskId: string | null | undefined) {
  return useQuery({
    queryKey: staffTaskCommentsQueryKey(taskId ?? null),
    enabled: Boolean(taskId),
    queryFn: async (): Promise<StaffTaskComment[]> => {
      if (!taskId) return [];
      const { data, error } = await supabase
        .from('staff_task_comments')
        .select('id, task_id, author_id, body, created_at, author:users!staff_task_comments_author_id_fkey(name,email)')
        .eq('task_id', taskId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      type Row = {
        id: string;
        task_id: string;
        author_id: string;
        body: string;
        created_at: string;
        author: UserJoin;
      };
      return ((data ?? []) as unknown as Row[]).map((row) => ({
        id: row.id,
        task_id: row.task_id,
        author_id: row.author_id,
        author_name: displayName(row.author) || row.author_id,
        body: row.body,
        created_at: row.created_at,
      }));
    },
    staleTime: 10_000,
  });
}

export function useAddStaffTaskComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { task_id: string; clinic_id: string; body: string }) => {
      const { error } = await supabase.from('staff_task_comments').insert({
        task_id: input.task_id,
        clinic_id: input.clinic_id,
        body: input.body.trim(),
      });
      if (error) throw error;
    },
    onSuccess: (_void, variables) => {
      queryClient.invalidateQueries({ queryKey: staffTaskCommentsQueryKey(variables.task_id) });
    },
  });
}
